import { useRef, useState } from "react";

import { TextArea } from "ui/text-area";
import { InfoMessage } from "ui/info-message";
import toast from "components/partials/toast/toast";
import CollegeProfileEditActions from "containers/admin/clients/client/college-profile-page/college-profile-edit-actions";
import CollegeProfileSectionHeader from "containers/admin/clients/client/college-profile-page/college-profile-section-header";

import {
  useClientConfiguration,
  useDeleteClientConfiguration,
  useSetClientConfiguration,
  useSystemConfiguration,
} from "hooks/api/use-client-configurations";
import { useParentEmailRules } from "hooks/use-parent-email-rules";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useUnsavedChangesGuard } from "hooks/use-unsaved-changes-guard";

import { getErrorMessage } from "utilities";
import { hasPermission } from "utilities/user";

import { ClientConfigurationDTOConfigKey } from "generated/schemas";
import { Permission } from "types/auth";

const CONFIG_KEY = ClientConfigurationDTOConfigKey.PARENT_EMAIL_PROMPT;

export const PARENT_EMAIL_RULES_VIEW = {
  CLIENT: "client",
  SYSTEM: "system",
} as const;

export type ParentEmailRulesView =
  (typeof PARENT_EMAIL_RULES_VIEW)[keyof typeof PARENT_EMAIL_RULES_VIEW];

export type CollegeProfileParentEmailRulesSectionProps = {
  clientId: string;
  view: ParentEmailRulesView;
};

/**
 * Renders one of Parent email rules' two nav-selectable sub-sections
 * (Client rules / System rules) - navigation between them lives in the left
 * nav (see `college-profile-nav.tsx`'s nested children), not an in-content
 * tab switcher.
 */
const CollegeProfileParentEmailRulesSection = ({
  clientId,
  view,
}: CollegeProfileParentEmailRulesSectionProps) => {
  const currentUser = useCurrentUser();
  const clientRuleValue = useParentEmailRules(clientId);
  // Shares the query cache/key with `useParentEmailRules` above - just surfaces
  // its loading state, no extra request.
  const { isLoading: isClientRuleLoading } = useClientConfiguration(clientId, CONFIG_KEY);
  const { data: systemConfiguration, isLoading: isSystemRuleLoading } = useSystemConfiguration(
    CONFIG_KEY,
  );
  const setClientConfiguration = useSetClientConfiguration();
  const deleteClientConfiguration = useDeleteClientConfiguration();

  const canWriteClient = hasPermission(currentUser, Permission.PERM_CLIENT_WRITE);

  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(clientRuleValue);
  const editStartValueRef = useRef(clientRuleValue);

  const isSaving = setClientConfiguration.isPending || deleteClientConfiguration.isPending;
  const isDraftDirty = draftValue !== editStartValueRef.current;

  // Called unconditionally, ahead of the System-rules early return below (that view has no
  // edit affordance at all), so this stays a valid hook call regardless of `view`.
  const { handleCancelClick, unsavedChangesDialog } = useUnsavedChangesGuard({
    isEditing,
    isDirty: isDraftDirty,
    onDiscard: () => setIsEditing(false),
  });

  const handleEdit = () => {
    editStartValueRef.current = clientRuleValue;
    setDraftValue(clientRuleValue);
    setIsEditing(true);
  };

  const handleDraftValueChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraftValue(event.target.value);
  };

  const handleSave = async () => {
    try {
      if (draftValue.trim()) {
        await setClientConfiguration.mutateAsync({
          clientId,
          configKey: CONFIG_KEY,
          configValue: draftValue,
        });
        toast.success({ data: { title: "Updated parent email rules" } });
      } else {
        await deleteClientConfiguration.mutateAsync({ clientId, configKey: CONFIG_KEY });
        toast.success({ data: { title: "Removed client specific parent email rules" } });
      }
      setIsEditing(false);
    } catch (error) {
      toast.error({
        data: {
          title: "Failed to save parent email rules",
          message: getErrorMessage(error?.response?.data),
        },
      });
    }
  };

  if (view === PARENT_EMAIL_RULES_VIEW.SYSTEM) {
    return (
      <div className="flex flex-col gap-2">
        <CollegeProfileSectionHeader title="System rules" />
        <div className="flex max-w-[600px] flex-col gap-3">
          <InfoMessage color="info">Rules will be applied during parent email generation.</InfoMessage>
          {isSystemRuleLoading ? (
            <p className="whitespace-pre-wrap text-sm text-gray-700">Loading...</p>
          ) : systemConfiguration?.configValue ? (
            <p className="whitespace-pre-wrap text-base font-medium text-gray-900">
              {systemConfiguration.configValue}
            </p>
          ) : (
            <p className="whitespace-pre-wrap text-base text-gray-500">
              There are no system rules yet.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {unsavedChangesDialog}
      <CollegeProfileSectionHeader title="Client rules">
        {canWriteClient && (
          <CollegeProfileEditActions
            isEditing={isEditing}
            isSaving={isSaving}
            isEditDisabled={isClientRuleLoading}
            onEdit={handleEdit}
            onCancel={handleCancelClick}
            onSave={handleSave}
          />
        )}
      </CollegeProfileSectionHeader>

      <div className="flex max-w-[600px] flex-col gap-3">
        <InfoMessage color="info">Rules will be applied during parent email generation.</InfoMessage>
        {isEditing ? (
          <TextArea value={draftValue} onChange={handleDraftValueChange} rows={13} />
        ) : clientRuleValue ? (
          <p className="whitespace-pre-wrap text-base font-medium text-gray-900">{clientRuleValue}</p>
        ) : (
          <p className="whitespace-pre-wrap text-base text-gray-500">
            There are no client rules yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default CollegeProfileParentEmailRulesSection;
